import React, { useCallback, useEffect, useRef, useState } from "react"
import { ConnectionUseCase } from "@/usecases/connection"
import { AssistRequestDialog, AssistConnectedDialog } from "@/widgets/AssistRequestDialog"

// 远程协助弹窗（全局常驻）：从 RemoteDeskView 提升到 App 根组件，
// 使登录页与远程页都能收到 assist 消息并弹窗（对齐 android 全局 Overlay）。
// 信令连接在登录成功后即建立（见 login_slice），assist 消息通道独立于 WebRTC，
// 此组件只负责订阅 onAssistCallbacks 并渲染弹窗，不依赖云电脑连接状态。
export const AssistDialogs: React.FC = () => {
  // 远程协助请求弹窗：收到 started 时记录发起方 srcDevId 并展示弹窗，
  // aborted/connected 关闭弹窗，connected 提示售后已连接（对齐 android）
  const [assistRequestSrc, setAssistRequestSrc] = useState<string | null>(null)
  // 售后已连接通知卡片（3 秒自动消失）
  const [assistConnectedOpen, setAssistConnectedOpen] = useState(false)
  // 已连接状态 ref：onAssistRequest 在 useEffect([]) 闭包内，需用 ref 读取最新
  // 值。售后已连接后短暂忽略后续 started，避免时序倒置导致请求卡片重新弹出
  // 且无法关闭（connected 先到、started 后到）。
  const assistConnectedRef = useRef(false)

  // 远程协助请求处理（对齐 android _onAssistRequest/_onAssistAborted/_onAssistConnected）
  const handleAssistConfirm = useCallback(() => {
    if (assistRequestSrc !== null) {
      ConnectionUseCase.sendAssistReply(assistRequestSrc, true)
      setAssistRequestSrc(null)
    }
  }, [assistRequestSrc])
  const handleAssistRefuse = useCallback(() => {
    if (assistRequestSrc !== null) {
      ConnectionUseCase.sendAssistReply(assistRequestSrc, false)
      setAssistRequestSrc(null)
    }
  }, [assistRequestSrc])

  useEffect(() => {
    const assistCallback = {
      onAssistRequest: (srcDevId: string | null) => {
        // 覆盖已存在的请求，避免重复弹窗（对齐 android _assistHandler?.close()）
        // 售后已连接后忽略后续 started，避免 connected 先到、started 后到
        // 导致请求卡片重新弹出且无法关闭
        if (assistConnectedRef.current) {
          return
        }
        setAssistRequestSrc(srcDevId ?? "")
      },
      onAssistAborted: () => {
        setAssistRequestSrc(null)
      },
      onAssistConnected: () => {
        // 售后已连接：关闭请求弹窗（用户已同意，请求卡片无意义）并展示已连接提示
        assistConnectedRef.current = true
        setAssistRequestSrc(null)
        setAssistConnectedOpen(true)
      },
    }
    ConnectionUseCase.addAssistCallback(assistCallback)
    return () => ConnectionUseCase.removeAssistCallback(assistCallback)
  }, [])

  // 协助已连接提示 3 秒后自动消失（对齐 android autoDismiss: true）
  useEffect(() => {
    if (!assistConnectedOpen) return
    const timer = window.setTimeout(() => {
      setAssistConnectedOpen(false)
      // 已连接卡片消失后，允许后续新的协助请求弹窗
      assistConnectedRef.current = false
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [assistConnectedOpen])

  return (
    <>
      {/* 远程协助请求弹窗（右上角通知卡片，已连接卡片出现时下移避让） */}
      <AssistRequestDialog
        isOpen={assistRequestSrc !== null}
        onConfirm={handleAssistConfirm}
        onCancel={handleAssistRefuse}
        shifted={assistConnectedOpen}
      />
      {/* 售后已连接提示（右上角通知卡片，3 秒自动消失） */}
      <AssistConnectedDialog isOpen={assistConnectedOpen} />
    </>
  )
}
